import React from "react";
import {
  AbsoluteFill,
  Audio,
  spring,
  staticFile,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";
import {
  ArrowDown,
  ArrowRight,
  Brain,
  CheckCircle2,
  CloudOff,
  Cpu,
  Flame,
  HardDrive,
  Laptop,
  MessageSquare,
  Server,
  Sparkles,
  Terminal,
  XCircle,
  Zap,
} from "lucide-react";
import { DynamicSubtitle, SubtitleItem } from "./components/DynamicSubtitle";
import {
  TeenageEngineeringBot,
  OPZuraPose,
} from "./components/AntiSlopHardwareMascots";

// Timed subtitles for Single Narrator (Tunggal) - 21.69s @ 30fps = 651 frames
const SUBTITLES: SubtitleItem[] = [
  {
    from: 0,
    to: 104,
    text: "Stop buang jutaan rupiah tiap bulan cuma buat sewa cloud atau langganan AI mahal!",
    highlight: "Stop buang jutaan sewa cloud AI mahal!",
  },
  {
    from: 108,
    to: 198,
    text: "Banyak yang mikir jalanin autonomous AI agent kayak gini wajib laptop dewa spek tiga puluh juta.",
    highlight: "wajib laptop dewa spek 30 juta?",
  },
  {
    from: 202,
    to: 228,
    text: "Padahal ini mitos!",
    highlight: "Padahal ini mitos!",
  },
  {
    from: 232,
    to: 385,
    text: "Laptop kantor RAM delapan giga atau mini PC bekas pun bisa lo sulap jadi workstation AI mandiri yang kerja dua puluh empat jam nonstop.",
    highlight: "RAM 8 giga kerja 24 jam nonstop",
  },
  {
    from: 390,
    to: 520,
    text: "Rahasianya, komputasi beratnya tetep dilempar via API, jadi laptop lo tetep enteng dan adem.",
    highlight: "komputasi di API • laptop tetep adem",
  },
  {
    from: 525,
    to: 651,
    text: "Mau setup sistem kayak gini di laptop lo? Komen SETUP, ntar gue bagi blueprint dan kodenya gratis!",
    highlight: "Komen SETUP • blueprint kodenya gratis!",
  },
];

export const ZuraStopCloudViral: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // Pose switching logic based on single narrator progression
  let currentPose: OPZuraPose = "pointing";
  if (frame >= 105 && frame < 200) {
    currentPose = "thinking";
  } else if (frame >= 200 && frame < 230) {
    currentPose = "talking";
  } else if (frame >= 230 && frame < 390) {
    currentPose = "typing";
  } else if (frame >= 390 && frame < 525) {
    currentPose = "idle";
  } else if (frame >= 525) {
    currentPose = "coffee";
  }

  return (
    <AbsoluteFill
      style={{
        backgroundColor: "#07080B",
        fontFamily:
          "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        color: "#F8FAFC",
        overflow: "hidden",
      }}
    >
      {/* 1. Voice Audio track: TUNGGAL / SINGLE NARRATOR from Samsung SM-A165F */}
      <Audio src={staticFile("vo_zura_tunggal.wav")} volume={1.0} />

      {/* 2. Ambient Tech Grid & Radial Glow */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage:
            "radial-gradient(circle at 50% 30%, rgba(0, 229, 255, 0.12), transparent 60%), radial-gradient(circle at 50% 80%, rgba(255, 59, 48, 0.08), transparent 50%)",
          opacity: 1,
        }}
      />
      <div
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)",
          backgroundSize: "60px 60px",
          opacity: 0.6,
        }}
      />

      {/* 3. Header / Branding Tag (Safe Zone Top Y: 220 - 320) */}
      <div
        style={{
          position: "absolute",
          top: 240,
          left: 80,
          right: 80,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          zIndex: 40,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            background: "rgba(255, 255, 255, 0.06)",
            border: "1px solid rgba(255, 255, 255, 0.12)",
            padding: "10px 18px",
            borderRadius: 100,
            backdropFilter: "blur(12px)",
          }}
        >
          <div
            style={{
              width: 10,
              height: 10,
              borderRadius: "50%",
              backgroundColor: "#00E5FF",
              boxShadow: "0 0 12px #00E5FF",
            }}
          />
          <span
            style={{
              fontSize: 22,
              fontWeight: 800,
              letterSpacing: "0.08em",
              color: "#F8FAFC",
            }}
          >
            ZURA • AI LAB
          </span>
        </div>

        <div
          style={{
            background: "rgba(255, 59, 48, 0.15)",
            border: "1px solid rgba(255, 59, 48, 0.35)",
            padding: "8px 16px",
            borderRadius: 100,
            color: "#FF453A",
            fontSize: 20,
            fontWeight: 700,
            letterSpacing: "0.04em",
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <CloudOff size={18} />
          <span>ZERO CLOUD BILL</span>
        </div>
      </div>

      {/* 4. DYNAMIC HERO SCENES (Frames 0 - 651) */}
      <div
        style={{
          position: "absolute",
          top: 360,
          left: 80,
          right: 80,
          height: 600,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 30,
        }}
      >
        {/* BEAT 1: HOOK MELEDAK (Frame 0 - 104) */}
        {frame < 105 && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              width: "100%",
            }}
          >
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 10,
                background: "#FF3B30",
                color: "#FFFFFF",
                padding: "12px 24px",
                borderRadius: 14,
                fontWeight: 900,
                fontSize: 28,
                letterSpacing: "0.06em",
                marginBottom: 28,
                boxShadow: "0 8px 30px rgba(255, 59, 48, 0.4)",
                transform: `scale(${spring({ frame, fps, config: { damping: 12 } })})`,
              }}
            >
              <Flame size={30} />
              <span>JANGAN BONCOS!</span>
            </div>

            <h1
              style={{
                fontSize: 66,
                fontWeight: 900,
                lineHeight: 1.15,
                margin: 0,
                textTransform: "uppercase",
                letterSpacing: "-0.02em",
                background: "linear-gradient(180deg, #FFFFFF 0%, #A0AEC0 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              STOP SEWA CLOUD <br />
              <span
                style={{
                  background:
                    "linear-gradient(90deg, #FF3B30 0%, #FF9500 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                JUTAAN RUPIAH!
              </span>
            </h1>

            <p
              style={{
                fontSize: 28,
                color: "#94A3B8",
                marginTop: 24,
                fontWeight: 500,
              }}
            >
              Modal laptop bekas kerja udah bisa jalanin AI otonom.
            </p>
          </div>
        )}

        {/* BEAT 2: MITOS SPEK DEWA (Frame 105 - 229) */}
        {frame >= 105 && frame < 230 && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              width: "100%",
              textAlign: "center",
            }}
          >
            <div
              style={{
                background: "rgba(0, 229, 255, 0.1)",
                border: "1px solid rgba(0, 229, 255, 0.3)",
                padding: "8px 20px",
                borderRadius: 100,
                color: "#00E5FF",
                fontSize: 22,
                fontWeight: 700,
                marginBottom: 24,
              }}
            >
              FAKTA ATAU MITOS?
            </div>

            <div
              style={{
                display: "flex",
                gap: 20,
                width: "100%",
                justifyContent: "center",
                marginTop: 10,
              }}
            >
              <div
                style={{
                  flex: 1,
                  background: "rgba(255, 59, 48, 0.08)",
                  border: "2px dashed rgba(255, 59, 48, 0.4)",
                  borderRadius: 24,
                  padding: "30px 20px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  opacity: 0.6,
                }}
              >
                <Server size={48} color="#FF3B30" style={{ marginBottom: 16 }} />
                <span style={{ fontSize: 22, fontWeight: 700, color: "#EF4444" }}>
                  Mitos: Spek 30 Juta
                </span>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10 }}>
                  <span style={{ fontSize: 18, color: "#94A3B8" }}>VPS Mahal</span>
                  <XCircle size={18} color="#FF3B30" />
                </div>
              </div>

              <div
                style={{
                  flex: 1,
                  background: "rgba(0, 229, 255, 0.12)",
                  border: "2px solid #00E5FF",
                  borderRadius: 24,
                  padding: "30px 20px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  boxShadow: "0 0 35px rgba(0, 229, 255, 0.2)",
                  transform: `scale(${spring({
                    frame: frame - 105,
                    fps,
                    config: { damping: 14 },
                  })})`,
                }}
              >
                <Laptop size={48} color="#00E5FF" style={{ marginBottom: 16 }} />
                <span style={{ fontSize: 24, fontWeight: 800, color: "#00E5FF" }}>
                  Realita: Laptop Kentang
                </span>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10 }}>
                  <span style={{ fontSize: 18, color: "#F8FAFC" }}>Cuma Butuh Python!</span>
                  <CheckCircle2 size={18} color="#00E5FF" />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* BEAT 3: WORKSTATION AI 8GB (Frame 230 - 389) */}
        {frame >= 230 && frame < 390 && (
          <div
            style={{
              width: "100%",
              background: "rgba(15, 23, 42, 0.8)",
              border: "1px solid rgba(0, 229, 255, 0.25)",
              borderRadius: 24,
              padding: "28px 32px",
              boxShadow: "0 20px 50px rgba(0, 0, 0, 0.6)",
              display: "flex",
              flexDirection: "column",
              gap: 16,
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                borderBottom: "1px solid rgba(255, 255, 255, 0.08)",
                paddingBottom: 16,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <Terminal size={24} color="#00E5FF" />
                <span
                  style={{
                    fontSize: 22,
                    fontWeight: 700,
                    letterSpacing: "0.04em",
                  }}
                >
                  HERMES-AGENT WORKSPACE
                </span>
              </div>
              <div
                style={{
                  background: "#10B981",
                  color: "#022C22",
                  fontSize: 16,
                  fontWeight: 900,
                  padding: "4px 12px",
                  borderRadius: 6,
                }}
              >
                RUNNING 24/7
              </div>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 14,
                marginTop: 8,
              }}
            >
              <div
                style={{
                  background: "rgba(255,255,255,0.04)",
                  padding: 16,
                  borderRadius: 14,
                }}
              >
                <span style={{ fontSize: 16, color: "#94A3B8" }}>RAM Minimal</span>
                <p
                  style={{
                    fontSize: 26,
                    fontWeight: 800,
                    color: "#00E5FF",
                    margin: "4px 0 0",
                  }}
                >
                  8 GB Cukup
                </p>
              </div>
              <div
                style={{
                  background: "rgba(255,255,255,0.04)",
                  padding: 16,
                  borderRadius: 14,
                }}
              >
                <span style={{ fontSize: 16, color: "#94A3B8" }}>Eksekusi Task</span>
                <p
                  style={{
                    fontSize: 26,
                    fontWeight: 800,
                    color: "#F59E0B",
                    margin: "4px 0 0",
                  }}
                >
                  100% Otonom
                </p>
              </div>
            </div>

            <div
              style={{
                background: "rgba(0, 0, 0, 0.5)",
                padding: "12px 18px",
                borderRadius: 12,
                fontFamily: "monospace",
                fontSize: 18,
                color: "#38BDF8",
                display: "flex",
                alignItems: "center",
                gap: 10,
              }}
            >
              <Cpu size={18} />
              <span>$ hermes agent --autonomous --target auto</span>
            </div>
          </div>
        )}

        {/* BEAT 4: RAHASIA CLOUD API (Frame 390 - 524) */}
        {frame >= 390 && frame < 525 && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              width: "100%",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 14,
                background: "rgba(16, 185, 129, 0.15)",
                border: "1px solid rgba(16, 185, 129, 0.35)",
                padding: "10px 24px",
                borderRadius: 100,
                color: "#10B981",
                fontSize: 22,
                fontWeight: 700,
                marginBottom: 20,
              }}
            >
              <CheckCircle2 size={24} />
              <span>LAPTOP TETAP ADEM (38°C)</span>
            </div>

            <h2
              style={{
                fontSize: 52,
                fontWeight: 900,
                lineHeight: 1.2,
                margin: "0 0 16px",
                textTransform: "uppercase",
              }}
            >
              KOMPUTASI DI API <br />
              <span style={{ color: "#00E5FF" }}>LAPTOP TETAP ENTENG</span>
            </h2>

            <p style={{ fontSize: 24, color: "#94A3B8", margin: 0 }}>
              Laptop cuma jadi remote controller, proses beratnya di Cloud.
            </p>
          </div>
        )}

        {/* BEAT 5: CTA HIGH-RETENTION (Frame 525 - 651) */}
        {frame >= 525 && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              width: "100%",
            }}
          >
            <h2
              style={{
                fontSize: 52,
                fontWeight: 900,
                lineHeight: 1.2,
                margin: "0 0 20px",
                textTransform: "uppercase",
              }}
            >
              KOMEN <span style={{ color: "#00E5FF" }}>"SETUP"</span> <br />
              DI BAWAH INI 👇
            </h2>

            <div
              style={{
                background: "rgba(255, 255, 255, 0.05)",
                border: "1px solid rgba(255, 255, 255, 0.15)",
                borderRadius: 20,
                padding: "18px 30px",
                display: "flex",
                alignItems: "center",
                gap: 16,
              }}
            >
              <MessageSquare size={32} color="#00E5FF" />
              <span style={{ fontSize: 26, fontWeight: 800, color: "#F8FAFC" }}>
                Gue bagi blueprint kodenya gratis!
              </span>
            </div>
          </div>
        )}
      </div>

      {/* 5. OFFICIAL MASCOT: OP-ZURA (TeenageEngineeringBot) */}
      <div
        style={{
          position: "absolute",
          top: 960,
          left: 0,
          right: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 35,
        }}
      >
        <TeenageEngineeringBot
          pose={currentPose}
          size={320}
          floating
        />

        {/* Mascot Name Badge */}
        <div
          style={{
            marginTop: 18,
            background: "rgba(15, 23, 42, 0.7)",
            border: "1px solid rgba(255, 255, 255, 0.1)",
            padding: "6px 18px",
            borderRadius: 100,
            fontSize: 16,
            fontWeight: 800,
            color: "#94A3B8",
            letterSpacing: "0.12em",
            textTransform: "uppercase",
          }}
        >
          MASKOT RESMI: OP-ZURA ({currentPose})
        </div>
      </div>

      {/* 6. DYNAMIC SUBTITLE SYSTEM (Safe Zone Bottom Y: 1440 - 1520) */}
      <DynamicSubtitle
        frame={frame}
        subtitles={SUBTITLES}
        highlightColor="#FFB020"
        fontSize={28}
        bottom={400}
      />
    </AbsoluteFill>
  );
};
